import { combineLatest, distinctUntilChanged, map, merge, Observable, of, shareReplay, startWith, switchMap, timer } from 'rxjs';

/**
 * Creates an observable which emits boolean values representing the busy state of a heavy process.
 *
 * @export
 * @param {On} on The observable representing the beginning of the heavy process.
 * @param {Off} off The observable representing the end of the heavy process.
 * @return {*}  {Observable<boolean>} An observable emitting `true` when the heavy process starts, and `false`
 * when the heavy process ends.
 */
export function busySignal<On extends Observable<any>, Off extends Observable<any>>(on: On, off: Off): Observable<boolean> {
    return merge(on.pipe(map(() => true)), off.pipe(map(() => false))).pipe(
        distinctUntilChanged(),
        // No refCount as the source can continue emitting without a problem
        shareReplay(1)
    );
}

/**
 * Takes multiple busy signals and combines them into a single value representing the busy state
 * of a mechanism which is composed of multiple processes.
 *
 * If at least one of the processes is still busy, this will emit `true`. Otherwise, this will emit `false`.
 *
 * @export
 * @param {...Observable<boolean>[]} busySignals The busy signal observables to combine into a single value.
 * @return {*}  {Observable<boolean>} An observable emitting `true` when at least one of the busy signals is still
 * `true`, and `false` when all busy signals have emitted `false`.
 */
export function combineBusySignals(...busySignals: Observable<boolean>[]): Observable<boolean> {
    return combineLatest(busySignals).pipe(
        map((areBusy) => areBusy.some(Boolean)),
        distinctUntilChanged(),
        // No refCount as the source may continue emitting without a problem
        shareReplay(1)
    );
}

/**
 * Creates an observable which emits `true` when a heavy process is still running but has shown no
 * sign of progress for a given time, and `false` otherwise.
 *
 * The countdown starts when the process becomes active and restarts on every `activity` emission.
 * When the process ends, this emits `false` immediately and ignores further activity.
 *
 * @export
 * @param {Observable<boolean>} active The busy state of the heavy process (e.g. a {@link busySignal}).
 * @param {Observable<unknown>} activity Emits whenever the process shows progress.
 * @param {number} silenceMs How long without activity counts as silent, in milliseconds.
 * @return {*}  {Observable<boolean>} An observable emitting `true` when the process is silent.
 */
export function silenceSignal(active: Observable<boolean>, activity: Observable<unknown>, silenceMs: number): Observable<boolean> {
    return active.pipe(
        distinctUntilChanged(),
        switchMap((isActive) =>
            isActive
                ? activity.pipe(
                      startWith(null),
                      switchMap(() =>
                          timer(silenceMs).pipe(
                              map(() => true),
                              startWith(false)
                          )
                      )
                  )
                : of(false)
        ),
        distinctUntilChanged(),
        // No refCount as the source can continue emitting without a problem
        shareReplay(1)
    );
}
